/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { CARD_PANEL_HEIGHT } from "./cardChrome";

/**
 * The card at the back of the deck: how you get another one.
 *
 * Always present, which is what makes the stack `n + 1`. Someone holding a
 * single card has nothing to swipe between and no way to learn that scanning a
 * receipt is what adds the next one — coins are minted at claim, so a customer
 * who never scans accrues nothing at all. This is the only place that fact
 * appears once the claim funnel is behind them.
 *
 * It borrows the pass chrome deliberately: in the stack it has to read as
 * another card, not as a banner that wandered in.
 */

const Step = defineComponent<{ index: number; icon: string; text: string }>({
  name: "HowToStep",
  props: ["index", "icon", "text"],
  setup(props) {
    return () => (
      <div class="flex items-start gap-3">
        <div class="w-7 h-7 rounded-full border border-current/30 bg-current/10 flex items-center justify-center shrink-0">
          <span class="material-symbols-outlined text-[15px] leading-none">
            {props.icon}
          </span>
        </div>
        <p class="font-mono text-xs leading-relaxed pt-1">{props.text}</p>
      </div>
    );
  },
});

export type HowToCardProps = {
  /** Drop the width cap and fill the container — the stack owns its own width. */
  wide?: boolean;
};

export const HowToCard = defineComponent<HowToCardProps>({
  name: "HowToCard",
  props: ["wide"],
  setup(props) {
    const { t } = useLocalization();

    return () => (
      <div
        class={[
          "bg-neutral text-neutral-content",
          "relative overflow-hidden rounded-3xl shadow-xl select-none",
          props.wide
            ? "w-full h-full"
            : "aspect-[5/7] w-full max-w-[20rem] mx-auto",
        ]}
      >
        <div
          aria-hidden="true"
          class="pointer-events-none absolute inset-0 bg-gradient-to-br from-current/15 via-transparent to-current/5"
        />
        <div
          aria-hidden="true"
          class="pointer-events-none absolute -right-10 -top-12 w-44 h-44 rounded-full bg-current/10"
        />

        <div class="relative h-full flex flex-col">
          {/* Tighter under the heading than a pass is: this card carries three
              wrapped sentences where a pass carries a stamp row, and in German
              on a short phone those sentences were spilling over the heading
              and down into the panel. */}
          <div class="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
            <div class="flex items-center gap-2 min-w-0">
              <span class="material-symbols-outlined text-xl shrink-0">
                add_card
              </span>
              <p class="font-mono text-[11px] uppercase tracking-[0.2em] truncate">
                {t("customer.collected.howto_eyebrow")}
              </p>
            </div>
          </div>

          {/* `safe center` rather than plain centring: centred content that is
              taller than its box overflows *both* ends, so on a short phone the
              clip guard was cutting the first step as well as the last. `safe`
              falls back to top-aligned exactly when it overflows, so the steps
              are read from the beginning and only the tail is lost. */}
          <div class="flex-1 flex [align-items:safe_center] px-5 min-h-0 overflow-hidden">
            {/* `overflow-hidden` is the guard, not the plan: the spacing above
                is what makes the steps fit, and this only ensures that a phone
                short enough to defeat that clips the last line instead of
                printing it on top of the panel. */}
            <div class="space-y-3 w-full">
              <Step
                index={1}
                icon="receipt_long"
                text={t("customer.collected.howto_step_1")}
              />
              <Step
                index={2}
                icon="qr_code_scanner"
                text={t("customer.collected.howto_step_2")}
              />
              <Step
                index={3}
                icon="redeem"
                text={t("customer.collected.howto_step_3")}
              />
            </div>
          </div>

          {/* The same panel as every other card, at the same height, so this
              one does not read as a taller card with a thin strip at the
              bottom. Its two lines sit at the foot of the panel, where a pass's
              programme and reward lines sit — centred in it rather than pinned
              to the bottom, since two lines pinned under a panel sized for six
              reads as a void rather than as space.

              No `backdrop-blur`: there is no photograph here to show through
              it, only a flat neutral fill, so the blur was buying nothing and
              costing a backdrop readback on every card in the deck. */}
          <div
            class={`mt-auto p-5 bg-current/10 border-t border-current/15 flex flex-col justify-center ${CARD_PANEL_HEIGHT}`}
          >
            <p class="font-mono text-[10px] uppercase tracking-widest opacity-60">
              {t("customer.collected.howto_footer_label")}
            </p>
            <p class="font-mono text-sm mt-0.5">
              {t("customer.collected.howto_footer_value")}
            </p>
          </div>
        </div>
      </div>
    );
  },
});
